import React, { useRef, useState, useCallback } from 'react';
import Slider from 'react-slick';
import { Pagination } from '@oneaudi/unified-web-components';
import { CarouselContainer, VehicleImage, PaginationContainer } from './ImageGallery.style';
import { ImagesParamsMap, ImageParamsMappingType } from './config';
import { ONDEMAND_CAROUSEL_GALLERY } from '../../constants/environment';
import { ImageType } from '../../types/oneGraphTypes';

interface ImageGalleryProps {
  images: ImageType[];
  isNewVehicle: boolean;
  isReservedVehicle: boolean | undefined;
  alt: string;
  bodyType: string | null;
}

const ImagesGalleryT3: React.FC<ImageGalleryProps> = ({
  images,
  isNewVehicle,
  isReservedVehicle,
  alt,
  bodyType,
}) => {
  const [galleryIndex, setIndex] = useState<number>(0);
  const sliderRef = useRef<Slider>(null);
  const [loadedImages, setLoadedImages] = useState<number[]>([]); // Keep track of loaded images
  const next = () => {
    sliderRef.current?.slickNext();
  };
  const previous = () => {
    sliderRef.current?.slickPrev();
  };

  const handleIndex = useCallback(
    (nextIndex: number) => {
      if (nextIndex === galleryIndex) return;
      setIndex(nextIndex);
    },
    [galleryIndex],
  );

  const handleImageLoad = (index: number[]) => {
    // Update the list of loaded images
    setLoadedImages((prevLoadedImages) => [...prevLoadedImages, index[0]]);
  };

  const hasDealerImages = !!images?.length;

  const renderFirstThreeImages = (images ?? []).slice(0, 3);

  const count = renderFirstThreeImages.length;

  return (
    <>
      <CarouselContainer
        isNew={isNewVehicle}
        tabIndex={0}
        isTier3
        hasDealerImages={hasDealerImages}
      >
        <Slider
          infinite
          speed={1000}
          slidesToShow={1}
          slidesToScroll={1}
          onLazyLoad={(slideIndex) => handleImageLoad(slideIndex)}
          beforeChange={(currentSlide, nextSlide) => {
            handleIndex(nextSlide);
          }}
          accessibility
          lazyLoad={ONDEMAND_CAROUSEL_GALLERY ? 'ondemand' : 'progressive'} // The other option that library provides is lazyLoad="ondemand"
          ref={sliderRef}
        >
          {renderFirstThreeImages.map((item, index) => {
            const imageParams = ImagesParamsMap[item.type as keyof ImageParamsMappingType] || '';
            const isImageLoaded = loadedImages.includes(index);
            return (
              <VehicleImage
                src={item.url + imageParams}
                alt={isImageLoaded ? `${alt}imageType-${item.type}-${index}` : ''}
                isNewVehicle={isNewVehicle}
                isReservedVehicle={isReservedVehicle}
                key={item.url || `${item.id.group}-${item.id.image}-${index}`}
                imageType={item.type || ''}
                bodyType={bodyType}
                isTier3
              />
            );
          })}
        </Slider>
      </CarouselContainer>
      {count > 1 && (
        <PaginationContainer isTier3>
          <Pagination
            current={galleryIndex + 1}
            decrementElement={{
              'aria-label': 'Decrease',
              onClick: () => previous(),
            }}
            incrementElement={{
              'aria-label': 'Increase',
              onClick: () => next(),
            }}
            itemCount={count}
            variant="compact"
          />
        </PaginationContainer>
      )}
    </>
  );
};

export default ImagesGalleryT3;
